import { Injectable, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, fromEvent, map, skip, take } from 'rxjs';
import { websocketErrorHandleInterceptor } from 'src/app/domain/customer-service/interceptors/error-handle-interceptor/websocket-error-handle-interceptor-fn';
import { IAtendimentoSocketDefaultResponse, IMessages, TAtendimentoSocketResponse } from 'src/app/domain/customer-service/models/atendimento-socket-models/atendimento-socket-models';
import { IWebSocketLogError } from 'src/app/domain/customer-service/models/usuario-socket-models/usuario-socket-models';
import { environment } from 'src/environments/environment';
import { UserApiService } from '../../api/user/user.api.service';
import { BsHotToastService } from '../../hot-toast/hot-toast.service';
import { QueryStringGenerator } from '../../utils/query-string-generator/query-string-generator';

@Injectable({
	providedIn: 'root',
})
@UntilDestroy()
export class AtendimentoSocketHandleService {
	private readonly API = environment.ATENDIMENTO_SOCKET;
	readonly CHATBOT_TOKEN_KEY = 'chatToken';
	private readonly STORAGE_KEY = 'mensgens_to_handle';
	private readonly SOCKET_START_MESSAGE = '{"protocol":"json","version":1}';
	private getInitialMessage(value: number): string {
		return `{"arguments":[${value}],"invocationId":"0","target":"receivemessage","type":1}`;
	}

	private messages$ = new BehaviorSubject<IMessages | undefined>(undefined);
	private socket?: WebSocket;
	private isSocketOpened: boolean = false;

	constructor(
		private readonly bsHotToast: BsHotToastService,
		private readonly injector: Injector,
	) { }

	startDomain(setorId?: number) {
		try {
			this.socket = this.createSocket(setorId ? setorId : 0);
			this.messageHandle(this.socket);
		} catch {
			this.bsHotToast.showError(
				'Ocorreu um erro ao conectar em Atendimentos! Por favor, entre em contato com um administrador.',
			);
		}
	}

	disconnectSocket() {
		if (this.socket) {
			this.socket.close();
			this.socket = undefined;
			this.isSocketOpened = false;
		}
	}

	get socketStatus() {
		return this.isSocketOpened;
	}

	get messages() {
		return this.messages$.value;
	}

	get messages$$() {
		return this.messages$.asObservable().pipe(filter((value) => !!value));
	}

	private messageHandle(socket?: WebSocket) {
		if (!socket) return;

		fromEvent<MessageEvent>(socket, 'message')
			.pipe(
				skip(1),
				untilDestroyed(this),
				map((event) => event.data as string),
				map((oldMessage) => oldMessage.substring(0, oldMessage.length - 1)),
			)
			.subscribe((message) => {
				let response: IAtendimentoSocketDefaultResponse;
				message.split('').forEach((splitedMessage) => {
					response = JSON.parse(splitedMessage || '{}');

					if (!this.isDefaultResponse(response)) return;

					response.arguments
						.map((argumentResponse) => ({
							...argumentResponse,
							value: this.parseArgumentValue(argumentResponse.value),
						}))
						.forEach((argument) => {
							this.setMessages(argument.value);
						});
				});
			});
	}

	private setMessages(mensagem: IMessages) {
		this.messages$.next(mensagem);
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.messages));
	}

	private createSocket(setorId: number) {
		const token = localStorage.getItem(this.CHATBOT_TOKEN_KEY);
		const query = QueryStringGenerator.generateQuery({ access_token: token });
		const socket = new WebSocket(`${this.API}${query}`);

		this.isSocketOpened = true;
		socket.onopen = () => {
			socket.send(this.SOCKET_START_MESSAGE);
		};

		fromEvent(socket, 'message')
			.pipe(take(1))
			.subscribe(() => {
				socket.send(this.getInitialMessage(setorId));
			});

		websocketErrorHandleInterceptor(
			socket,
			(code) => this.handleSocketError(code)
		)

		return socket;
	}

	private parseArgumentValue(value: string | IMessages) {
		return typeof value == 'string' ? (JSON.parse(value) as IMessages) : value;
	}

	private isDefaultResponse(
		data: TAtendimentoSocketResponse,
	): data is IAtendimentoSocketDefaultResponse {
		return (data as IAtendimentoSocketDefaultResponse)?.target == 'receivemessage';
	}

	private handleSocketError(code: number) {
		const userApiService = this.injector.get(UserApiService);
		const user = userApiService.currentUser$$.value;

		if (user) {
			const payload: IWebSocketLogError = {
				seqCliente: user.seq_cliente,
				usuarioId: user.id,
				statusCode: code,
			};

			userApiService.webSocketLog(payload).subscribe();
		}
	}
}
